/**
 * ed-card-size-selector-guard.js
 * Embedded Designz — permanent custom asset (never overwritten by theme updates)
 *
 * WHAT THIS FIXES:
 * Stretch's built-in "floating product card size selector" (Theme settings ->
 * Product cards -> "Show product card size selector") renders one tiny form
 * per variant directly on product cards (collection pages, "you may also
 * like", etc.) inside .product-card__floating-size-selector. Each form only
 * carries a variant id + quantity=1 and submits straight to /cart/add. Easify
 * Options' required-fields UI (Shirt Color, Thread Color, personalization
 * text, etc.) never renders anywhere near this markup, so a single tap on a
 * size button on a collection page added a customized product to cart with
 * ZERO of its required custom properties captured — silently, with no
 * indication to the shopper anything was skipped.
 *
 * This is a different bypass than the sticky-Add-to-Cart-bar issue handled by
 * ed-sticky-atc-fix.js (see snippets/ed-patch-notes.liquid FILE 13) and a
 * different bypass than the known Quick Buy modal gap (FILE 13 "Known
 * Remaining Gap") — this one is the native card-level size selector, and
 * unlike those two, there are no required-option fields anywhere in this
 * DOM subtree to check, because the options UI never renders here at all.
 * There is nothing to validate against — so instead of adding to cart, we
 * send the shopper to the real product page with that size pre-selected,
 * where Easify's options render normally and required fields are enforced
 * exactly as they are for a normal product-page Add to Cart.
 *
 * HOW IT WORKS:
 * A capturing 'submit' listener on document (fires before the form's own
 * submit handling, immune to script load order) checks whether the submitted
 * form lives inside a .product-card__floating-size-selector container. If
 * so, it blocks the native submit (preventDefault + stopPropagation) and
 * navigates to the product page instead, with ?variant=<id> so the correct
 * size is pre-selected on arrival.
 *
 * The product handle comes from the closest <product-card handle="..."> — a
 * custom element Stretch already renders around every card (see
 * snippets/product-card.liquid). The variant id comes from the form's own
 * hidden <input name="id">, which is always present (that's literally what
 * the native /cart/add submission uses).
 *
 * FAIL-SAFE, NOT FAIL-OPEN: if the handle or variant id can't be read for
 * any reason, the submit is still blocked (just not redirected) rather than
 * being allowed through to /cart/add — this script's whole job is making
 * sure this path can never silently add a customized product without its
 * required options, so when in doubt it blocks.
 *
 * SCOPE: sitewide, all product cards, current and future. Pure DOM behavior,
 * no per-product/per-template configuration, no dependency on Easify beyond
 * "the product page enforces its own required fields normally" (which it
 * already does for a direct product-page Add to Cart).
 *
 * PERFORMANCE: single small deferred script, one delegated 'submit' listener
 * on document, no scroll/resize listeners, no polling, no additional HTTP
 * requests beyond this one asset file.
 */
(function () {
  'use strict';

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      var container = form.closest('.product-card__floating-size-selector');

      if (!container) {
        return;
      }

      // This is a card-level quick-size-add submit. Required options never
      // render in this DOM subtree, so this path must never reach
      // /cart/add directly — always block first, then try to redirect.
      event.preventDefault();
      event.stopPropagation();

      var card = form.closest('product-card');
      var handle = card ? card.getAttribute('handle') : null;
      var variantInput = form.querySelector('input[name="id"]');
      var variantId = variantInput ? variantInput.value : null;

      if (!handle || !variantId) {
        // Can't safely build a product URL — fail closed (submit already
        // blocked above) and stop here rather than guessing a destination.
        return;
      }

      var url = '/products/' + encodeURIComponent(handle) + '?variant=' + encodeURIComponent(variantId);
      window.location.href = url;
    },
    true
  );
})();
